import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MONTHS = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
const DAYS = ["Pzt","Sal","Çar","Per","Cum","Cmt","Paz"];

function statusFor(dateStr, attendance, leaves, holidays) {
  if (holidays?.includes(dateStr)) return { key: "holiday", label: "Resmi Tatil", cls: "bg-purple-500/25 text-purple-300 border-purple-500/40" };
  const onLeave = leaves.find((l) => dateStr >= l.start_date && dateStr <= l.end_date);
  if (onLeave) return { key: "leave", label: `İzin (${onLeave.type})`, cls: "bg-blue-500/25 text-blue-300 border-blue-500/40" };
  const att = attendance.find((a) => a.date === dateStr);
  if (att) {
    if ((att.late_minutes || 0) > 0) return { key: "late", label: `Geç ${att.late_minutes} dk`, cls: "bg-amber-500/25 text-amber-300 border-amber-500/40" };
    return { key: "normal", label: "Normal", cls: "bg-emerald-500/25 text-emerald-300 border-emerald-500/40" };
  }
  const d = new Date(dateStr);
  const dow = d.getDay();
  if (dow === 0 || dow === 6) return null;
  if (dateStr > new Date().toISOString().slice(0, 10)) return null;
  return { key: "absent", label: "Gelmedi", cls: "bg-red-500/25 text-red-300 border-red-500/40" };
}

export default function CalendarPage() {
  const { user } = useAuth();
  const [now, setNow] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState(user?.role === "employee" ? user.employee_id : "all");
  const [data, setData] = useState({ attendance: [], leaves: [], holidays: [] });

  useEffect(() => {
    if (user?.role !== "employee") {
      api.get("/employees").then(({ data }) => setEmployees(data));
    }
  }, [user]);

  useEffect(() => {
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    const params = { year: y, month: m };
    if (employeeId && employeeId !== "all") params.employee_id = employeeId;
    api.get("/attendance/calendar", { params }).then(({ data }) => setData(data));
  }, [now, employeeId]);

  const grid = useMemo(() => {
    const y = now.getFullYear();
    const m = now.getMonth();
    const first = new Date(y, m, 1);
    const startDow = (first.getDay() + 6) % 7; // Mon=0
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const ds = `${y}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
      cells.push({ day: d, date: ds });
    }
    while (cells.length % 7) cells.push(null);
    return cells;
  }, [now]);

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Takvim</h1>
          <p className="text-muted-foreground text-sm mt-1">Devam durumu takvimi</p>
        </div>
        {user?.role !== "employee" && (
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger className="w-64" data-testid="calendar-employee-select">
              <SelectValue placeholder="Tüm personel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm personel (özet)</SelectItem>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="glass-card rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <Button variant="outline" size="icon" onClick={() => setNow(new Date(now.getFullYear(), now.getMonth() - 1, 1))} data-testid="prev-month">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="font-display text-xl font-bold tracking-tight" data-testid="current-month">
            {MONTHS[now.getMonth()]} {now.getFullYear()}
          </div>
          <Button variant="outline" size="icon" onClick={() => setNow(new Date(now.getFullYear(), now.getMonth() + 1, 1))} data-testid="next-month">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <div className="grid grid-cols-7 gap-2 mb-2">
          {DAYS.map((d) => (
            <div key={d} className="text-xs uppercase tracking-wider text-muted-foreground text-center py-2">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {grid.map((c, i) => {
            if (!c) return <div key={i} />;
            const st = employeeId === "all"
              ? null
              : statusFor(c.date, data.attendance, data.leaves, data.holidays);
            const isToday = c.date === new Date().toISOString().slice(0, 10);
            return (
              <div
                key={c.date}
                data-testid={`day-${c.date}`}
                className={`aspect-square rounded-lg border p-2 flex flex-col ${
                  isToday ? "border-blue-500 border-2" : "border-border/60"
                } ${st ? st.cls : "bg-secondary/40"}`}
              >
                <div className="text-sm font-semibold">{c.day}</div>
                {st && <div className="text-[10px] mt-auto leading-tight">{st.label}</div>}
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex flex-wrap gap-3 text-xs">
          <Legend cls="bg-emerald-500/25 text-emerald-300 border-emerald-500/40" label="Normal" />
          <Legend cls="bg-amber-500/25 text-amber-300 border-amber-500/40" label="Geç" />
          <Legend cls="bg-red-500/25 text-red-300 border-red-500/40" label="Gelmedi" />
          <Legend cls="bg-blue-500/25 text-blue-300 border-blue-500/40" label="İzinli" />
          <Legend cls="bg-purple-500/25 text-purple-300 border-purple-500/40" label="Resmi Tatil" />
        </div>
      </div>
    </div>
  );
}

function Legend({ cls, label }) {
  return (
    <div className={`px-3 py-1.5 rounded-full border ${cls}`}>{label}</div>
  );
}
